/**
 * Tech-debt #4 — Firmware / diagnostics / log family extracted from
 * csmsCallHandler. The simulator has no real firmware or log files, so each
 * action replies an accept + filename and then runs a synthetic, observable
 * lifecycle (status notifications, with a best-effort fetch POST of an Event
 * Log blob for the upload variants).
 */
import { errorMessage } from '@/lib/errorMessage';
import { sha256HexBytes } from '@/lib/sha256';
import type { OcppAdapter } from '@/protocol/OcppAdapter';
import { OcppV201Adapter } from '@/protocol/v201/OcppV201Adapter';
import { OcppV16Adapter } from '@/protocol/v16/OcppV16Adapter';
import { useDiagnosticsStore } from '../useDiagnosticsStore';
import { useEventLogStore } from '../useEventLogStore';
import { useFirmwareStore } from '../useFirmwareStore';
import { useStationStore } from '../useStationStore';
import type { CsmsCtx, CsmsEvent } from './context';

export function handleUpdateFirmware(event: CsmsEvent<'UpdateFirmware'>, ctx: CsmsCtx): void {
  // FirmwareManagement, both protocols. v1.6 reply is empty; v2.0.1 replies an
  // UpdateFirmwareStatus. Then run the lifecycle: a REAL fetch of the firmware
  // image (Downloading → Downloaded / DownloadFailed), then a synthetic
  // Installing → Installed (the sim can't actually flash anything).
  if (ctx.adapter instanceof OcppV201Adapter) {
    ctx.dispatcher.reply(ctx.messageId, { status: 'Accepted' });
  } else {
    ctx.dispatcher.reply(ctx.messageId, {});
  }
  void runFirmwareLifecycle(ctx.adapter, event.requestId, event.location);
}

export function handlePublishFirmware(
  event: CsmsEvent<'PublishFirmware'>,
  ctx: CsmsCtx,
): void {
  // Local Controller role, v2.0.1-only. Reply Accepted (Rejected for an empty
  // location — the schema's GenericStatus has no InvalidUrl), then run a
  // synthetic Downloading → Downloaded → ChecksumVerified → Published lifecycle
  // via PublishFirmwareStatusNotification echoing the requestId.
  if (!(ctx.adapter instanceof OcppV201Adapter)) {
    ctx.dispatcher.replyError(
      ctx.messageId,
      'NotImplemented',
      'PublishFirmware is a v2.0.1 Local Controller action',
      {},
    );
    return;
  }
  if (event.location.trim() === '') {
    ctx.dispatcher.reply(ctx.messageId, {
      status: 'Rejected',
      statusInfo: { reasonCode: 'InvalidLocation' },
    });
    return;
  }
  ctx.dispatcher.reply(ctx.messageId, { status: 'Accepted' });
  void runPublishFirmwareLifecycle(ctx.adapter, event.requestId, event.location);
}

export function handleUnpublishFirmware(
  _event: CsmsEvent<'UnpublishFirmware'>,
  ctx: CsmsCtx,
): void {
  // Local Controller role, v2.0.1-only. The simulator keeps no persistent
  // published image (PublishFirmware runs a synthetic lifecycle), so there is
  // nothing to tear down — acknowledge `Unpublished`. Status enum:
  // DownloadOngoing | NoFirmware | Unpublished.
  if (!(ctx.adapter instanceof OcppV201Adapter)) {
    ctx.dispatcher.replyError(
      ctx.messageId,
      'NotImplemented',
      'UnpublishFirmware is a v2.0.1 Local Controller action',
      {},
    );
    return;
  }
  ctx.dispatcher.reply(ctx.messageId, { status: 'Unpublished' });
}

export function handleGetLog(event: CsmsEvent<'GetLog'>, ctx: CsmsCtx): void {
  // v2.0.1-only. No real log files: reply Accepted + a filename, then
  // synthesise a JSON blob of recent Event Log rows, POST it to the
  // CSMS-supplied remoteLocation, and emit LogStatusNotification progress.
  if (!(ctx.adapter instanceof OcppV201Adapter)) {
    ctx.dispatcher.replyError(ctx.messageId, 'NotImplemented', 'GetLog is v2.0.1-only', {});
    return;
  }
  const filename = `evse3-log-${event.requestId}.json`;
  ctx.dispatcher.reply(ctx.messageId, { status: 'Accepted', filename });
  void runLogUpload(ctx.adapter, event, filename);
}

export function handleGetDiagnostics(event: CsmsEvent<'GetDiagnostics'>, ctx: CsmsCtx): void {
  // OCPP 1.6J only (v2.0.1 uses GetLog). Synthetic diagnostics upload
  // mirroring runLogUpload: reply a fileName, then
  // DiagnosticsStatusNotification(Uploading → Uploaded / UploadFailed).
  if (!(ctx.adapter instanceof OcppV16Adapter)) {
    ctx.dispatcher.replyError(
      ctx.messageId,
      'NotImplemented',
      'GetDiagnostics is OCPP 1.6J-only',
      {},
    );
    return;
  }
  const fileName = `evse3-diagnostics-${Date.now()}.json`;
  ctx.dispatcher.reply(ctx.messageId, { fileName });
  void runDiagnosticsUpload(ctx.adapter, event, fileName);
}

const FIRMWARE_DELAY_MS = 30;
const firmwareDelay = (): Promise<void> =>
  new Promise((resolve) => setTimeout(resolve, FIRMWARE_DELAY_MS));

/** The five FirmwareStatus values this lifecycle emits — valid on both 1.6 + 2.x. */
type FirmwareLifecycleStatus =
  | 'Downloading'
  | 'Downloaded'
  | 'DownloadFailed'
  | 'Installing'
  | 'Installed';

/** Last path segment of the URL, or a requestId-derived fallback. */
function firmwareFilename(location: string, requestId: number | undefined): string {
  try {
    const last = new URL(location).pathname.split('/').filter(Boolean).pop();
    if (last !== undefined && last !== '') return last;
  } catch {
    /* not a parseable URL — fall through */
  }
  return `firmware-${requestId ?? 'image'}.bin`;
}

/**
 * Firmware lifecycle. The download is REAL: the CP fetches the firmware image
 * from `location`, records it (size + SHA-256) in useFirmwareStore so the tester
 * can save + verify it, and reports Downloading → Downloaded. A fetch failure
 * (network / CORS / non-2xx) reports DownloadFailed and stops. On success the
 * install step stays synthetic (Installing → Installed — the sim can't flash).
 * Version-aware: v2.0.1 carries the requestId. Fire-and-forget; unexpected
 * errors land on `lastError`.
 */
async function runFirmwareLifecycle(
  adapter: OcppAdapter,
  requestId: number | undefined,
  location: string,
): Promise<void> {
  const fw = useFirmwareStore.getState();
  const emit = async (status: FirmwareLifecycleStatus): Promise<void> => {
    if (adapter instanceof OcppV201Adapter) {
      await adapter.firmwareStatusNotification({
        status,
        ...(requestId !== undefined ? { requestId } : {}),
      }).response;
    } else if (adapter instanceof OcppV16Adapter) {
      await adapter.firmwareStatusNotification(status).response;
    }
  };

  try {
    fw.startDownload(location);
    await firmwareDelay();
    await emit('Downloading');

    // Real download — browser-bounded (cross-origin without CORS → failure).
    let blob: Blob;
    try {
      if (typeof fetch !== 'function') throw new Error('fetch unavailable');
      const res = await fetch(location);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      blob = await res.blob();
    } catch (e) {
      useFirmwareStore.getState().setFailed(errorMessage(e));
      await emit('DownloadFailed');
      return;
    }

    const bytes = new Uint8Array(await blob.arrayBuffer());
    useFirmwareStore.getState().setDownloaded({
      filename: firmwareFilename(location, requestId),
      sizeBytes: blob.size,
      contentType: blob.type !== '' ? blob.type : 'application/octet-stream',
      sha256: sha256HexBytes(bytes),
      blob,
    });
    await emit('Downloaded');

    // Synthetic install — nothing to actually flash.
    useFirmwareStore.getState().setInstalling();
    await firmwareDelay();
    await emit('Installing');
    useFirmwareStore.getState().setInstalled();
    await firmwareDelay();
    await emit('Installed');
  } catch (err) {
    useStationStore.setState({ lastError: errorMessage(err) });
  }
}

/**
 * Synthetic Local Controller publish lifecycle: Downloading → Downloaded →
 * ChecksumVerified → Published with small delays so the progression is
 * observable in the Event Log. The terminal `Published` status carries the
 * published location(s) (schema requires ≥ 1 there); every step echoes the
 * requestId. Fire-and-forget; errors land on `lastError`.
 */
async function runPublishFirmwareLifecycle(
  adapter: OcppV201Adapter,
  requestId: number,
  location: string,
): Promise<void> {
  const steps = ['Downloading', 'Downloaded', 'ChecksumVerified', 'Published'] as const;
  try {
    for (const status of steps) {
      await firmwareDelay();
      await adapter.publishFirmwareStatusNotification({
        status,
        requestId,
        ...(status === 'Published' ? { location: [location] } : {}),
      }).response;
    }
  } catch (err) {
    useStationStore.setState({ lastError: errorMessage(err) });
  }
}

/**
 * Synthetic OCPP 1.6J diagnostics upload. Mirrors runLogUpload:
 * DiagnosticsStatusNotification(Uploading) → POST a JSON blob of recent Event
 * Log rows to the CSMS-supplied location → Uploaded / UploadFailed.
 */
async function runDiagnosticsUpload(
  adapter: OcppV16Adapter,
  event: CsmsEvent<'GetDiagnostics'>,
  fileName: string,
): Promise<void> {
  // Generate the bundle and make it locally downloadable (the browser can't
  // reach an ftp:// target; most http targets fail CORS). The reported status
  // reflects the REAL upload result; the tester can still Save to disk and use
  // the manual Mark-Uploaded to drive the CSMS happy-path.
  const text = JSON.stringify(
    { fileName, entries: useEventLogStore.getState().entries },
    null,
    2,
  );
  useDiagnosticsStore
    .getState()
    .record({ kind: 'diagnostics', location: event.location, filename: fileName, text });
  try {
    await adapter.diagnosticsStatusNotification('Uploading').response;
    const ok = await tryUpload(event.location, text);
    if (ok) useDiagnosticsStore.getState().setUploaded();
    else useDiagnosticsStore.getState().setFailed('Upload target unreachable from the browser.');
    await adapter.diagnosticsStatusNotification(ok ? 'Uploaded' : 'UploadFailed').response;
  } catch (err) {
    useDiagnosticsStore.getState().setFailed(errorMessage(err));
    try {
      await adapter.diagnosticsStatusNotification('UploadFailed').response;
    } catch {
      /* best-effort */
    }
    useStationStore.setState({ lastError: errorMessage(err) });
  }
}

/** Best-effort HTTP(S) POST of the bundle. Returns false on any failure. */
async function tryUpload(location: string, body: string): Promise<boolean> {
  if (typeof fetch !== 'function') return false;
  try {
    const res = await fetch(location, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body,
    });
    return res.ok;
  } catch {
    return false; // ftp:// scheme, CORS, network — all unreachable from a browser
  }
}

/**
 * Synthetic GetLog upload. Fires LogStatusNotification (Uploading → Uploaded /
 * UploadFailure) and POSTs a JSON blob of recent Event Log rows to the
 * CSMS-supplied URL. Best-effort; errors land on `lastError`.
 */
async function runLogUpload(
  adapter: OcppV201Adapter,
  event: CsmsEvent<'GetLog'>,
  filename: string,
): Promise<void> {
  const reqId = event.requestId;
  const rows = useEventLogStore.getState().entries;
  const filtered = rows.filter((r) => {
    const iso = new Date(r.at).toISOString();
    if (event.oldestTimestamp !== undefined && iso < event.oldestTimestamp) return false;
    if (event.latestTimestamp !== undefined && iso > event.latestTimestamp) return false;
    return true;
  });
  const text = JSON.stringify({ filename, logType: event.logType, entries: filtered }, null, 2);
  useDiagnosticsStore.getState().record({
    kind: 'log',
    location: event.remoteLocation,
    filename,
    text,
    requestId: reqId,
  });
  try {
    await adapter.logStatusNotification({ status: 'Uploading', requestId: reqId }).response;
    const ok = await tryUpload(event.remoteLocation, text);
    if (ok) useDiagnosticsStore.getState().setUploaded();
    else useDiagnosticsStore.getState().setFailed('Upload target unreachable from the browser.');
    await adapter.logStatusNotification({
      status: ok ? 'Uploaded' : 'UploadFailure',
      requestId: reqId,
    }).response;
  } catch (err) {
    useDiagnosticsStore.getState().setFailed(errorMessage(err));
    try {
      await adapter.logStatusNotification({ status: 'UploadFailure', requestId: reqId }).response;
    } catch {
      /* swallow — the upload-failure notify is itself best-effort */
    }
    useStationStore.setState({ lastError: errorMessage(err) });
  }
}
